import React, { useEffect, useState } from "react";
import { floorPositionView } from "../../lib/api";
import { px, maskText } from "./floorUtil";

const NoData = ({ what }) => <div className="h-full flex items-center justify-center fc-label">no data{what ? ` · ${what}` : ""}</div>;

const Chart = ({ v }) => {
  const c = v.candles || [];
  if (c.length < 2) return <NoData what="candles" />;
  const lv = [["ENTRY", v.entry, "#00F0FF"], ["TARGET", v.target, "#2CF6B3"], ["STOP", v.stop, "#FF3D68"]].filter((l) => l[1] != null && !Number.isNaN(Number(l[1])));
  const all = [...c.flatMap((k) => [k.high, k.low]), ...lv.map((l) => Number(l[1]))];
  const lo = Math.min(...all), hi = Math.max(...all), span = hi - lo || 1;
  const y = (p) => 96 - ((p - lo) / span) * 92, w = 100 / c.length;
  const sma = c.map((_, i) => (i < 19 ? null : c.slice(i - 19, i + 1).reduce((s, k) => s + k.close, 0) / 20));
  const smaPts = sma.map((s, i) => (s == null ? null : `${i * w + w / 2},${y(s)}`)).filter(Boolean).join(" ");
  return (
    <div className="relative h-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {c.map((k, i) => { const up = k.close >= k.open, col = up ? "#2CF6B3" : "#FF3D68", x = i * w + w / 2;
          return (<g key={i}><line x1={x} x2={x} y1={y(k.high)} y2={y(k.low)} stroke={col} strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
            <rect x={x - w * 0.35} width={w * 0.7} y={y(Math.max(k.open, k.close))} height={Math.max(0.4, Math.abs(y(k.open) - y(k.close)))} fill={col} /></g>); })}
        {smaPts && <polyline points={smaPts} fill="none" stroke="#FACC15" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />}
        {lv.map((l) => <line key={l[0]} x1="0" x2="100" y1={y(Number(l[1]))} y2={y(Number(l[1]))} stroke={l[2]} strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />)}
      </svg>
      <div className="absolute top-1 right-1 flex flex-col items-end gap-0.5">
        {lv.map((l) => <span key={l[0]} className="fc-num text-[9px] px-1 rounded bg-black/70" style={{ color: l[2] }}>{l[0]} {px(l[1])}</span>)}
        <span className="fc-num text-[9px] px-1 rounded bg-black/70 text-[#FACC15]">SMA20</span>
      </div>
    </div>
  );
};

const FUND = [["Fwd P/E", "forward_pe"], ["P/E", "trailing_pe"], ["Profit mgn %", "profit_margin"], ["Op mgn %", "operating_margin"]];

const Fundamentals = ({ v, hidden }) => {
  const f = v.fundamentals || {}, have = FUND.filter(([, k]) => f[k] != null);
  const bullets = (v.reasons || []).slice(0, 4);
  return (
    <div className="h-full flex flex-col gap-1.5 min-h-0" data-testid="floor-slide-fundamentals">
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {have.length ? have.map(([l, k]) => <span key={k} className="fc-num text-[10px] text-cyan-200/70">{l} <b className="text-cyan-50">{f[k]}</b></span>)
          : <span className="fc-label">fundamentals · no data</span>}
        {f.insider_flow && f.insider_flow !== "n/a" && <span className="fc-num text-[10px] text-cyan-200/70">{f.insider_flow}</span>}
        {(v.insider || []).map((r, i) => <span key={i} className="fc-num text-[10px] text-amber-300/80">finviz insider: {maskText(Object.values(r).slice(0, 4).join(" · "), hidden)}</span>)}
      </div>
      {bullets.length ? (
        <ul className="space-y-1 overflow-y-auto min-h-0" data-testid="floor-slide-reasons">
          {bullets.map((b) => <li key={b.k} className="text-[11px] leading-snug text-cyan-50/85 flex gap-1.5"><span className="text-[#00F0FF]">•</span><span className="line-clamp-3">{maskText(b.text, hidden)}</span></li>)}
        </ul>
      ) : <NoData what="agent reasoning" />}
    </div>
  );
};

export const SlideChart = ({ positions, loop, hidden }) => {
  const open = [...(positions || [])].filter((p) => p.status === "open").sort((a, b) => Number(b.notional || 0) - Number(a.notional || 0));
  const pos = open.length ? open[loop % open.length] : null;
  const [cache, setCache] = useState({});
  useEffect(() => {
    if (!pos || cache[pos.id]) return;
    floorPositionView(pos.id).then((v) => setCache((c) => ({ ...c, [pos.id]: v }))).catch(() => setCache((c) => ({ ...c, [pos.id]: { failed: true } })));
  }, [pos?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pos) return <NoData what="no open positions" />;
  const v = cache[pos.id];
  return (
    <div className="grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-2 gap-2 h-full" data-testid="floor-slide-chart">
      <div className="min-h-0 rounded-xl border border-cyan-300/15 p-2 flex flex-col">
        <p className="fc-label mb-1">Technical · <span className="text-cyan-50">{pos.symbol}</span> · {String(pos.side || "").toUpperCase()}</p>
        <div className="flex-1 min-h-0">{!v ? <NoData what="loading" /> : v.failed ? <NoData /> : <Chart v={v} />}</div>
      </div>
      <div className="min-h-0 rounded-xl border border-cyan-300/15 p-2 flex flex-col">
        <p className="fc-label mb-1">Fundamental · why the floor holds it</p>
        <div className="flex-1 min-h-0">{!v ? <NoData what="loading" /> : v.failed ? <NoData /> : <Fundamentals v={v} hidden={hidden} />}</div>
      </div>
    </div>
  );
};
